import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Button,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RangePickerProps } from "antd/es/date-picker";
import { logEntry } from "src/layouts/AdminPortal/ActivityLogsPage/ActivityLogsInterface";
import { getUserActivityLogs } from "../../../services/AdminPortalAPIs"; 
import dayjs from "dayjs";
import Papa from "papaparse";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledTable from "../../../components/reusable/StyledTable";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledInput from "src/components/reusable/StyledInput";


const { RangePicker } = DatePicker;

const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const columns: ColumnsType<logEntry> = [
  { title: "User", dataIndex: "user", key: "user" },
  {
    title: "Event",
    dataIndex: "event",
    key: "event",
    render: (event: string) => toTitleCase(event),
  },
  {
  title: "Details",
  dataIndex: "details",
  key: "details",
  align: "left",
  render: text => <div style={{ textAlign: "left" }}>{text}</div>
},
  { title: "Date & Time", dataIndex: "dateTime", key: "dateTime" },
  { title: "IP Address", dataIndex: "ipAddress", key: "ipAddress" },
];


const ActivityLogs: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [eventFilter, setEventFilter] = useState<string | undefined>();
  const [logsData, setLogsData] = useState<logEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultEnd = dayjs();
  const defaultStart = defaultEnd.subtract(3, "day");

  const [dateRange, setDateRange] = useState<RangePickerProps["value"]>([
    defaultStart,
    defaultEnd,
  ]);

  const [currentPage, setCurrentPage] = useState(1);
const pageSize = 7;

  const resetFilters = () => {
    setSearchText("");
    setEventFilter(undefined);
    setDateRange([defaultStart, defaultEnd]);
  };

  const exportToCSV = (data: logEntry[]) => {
    const csv = Papa.unparse(
      data.map(({ user, event, details, dateTime, ipAddress }) => ({
        User: user,
        Event: event,
        Details: details,
        "Date & Time": dateTime,
        "IP Address": ipAddress,
      }))
    );

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `ActivityLogs_${dayjs().format("YYYY-MM-DD")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchActivityLogs = async () => {
  if (
    !dateRange ||
    dateRange.length !== 2 ||
    !dateRange[0] ||
    !dateRange[1]
  )
    {return;}

  const [start, end] = dateRange;

 const payload = {
  start_date: start.startOf("day").format("YYYY-MM-DD"),
  end_date: end.endOf("day").format("YYYY-MM-DD"),
};

  try {
    setLoading(true);
    setError(null); 
    const response = await getUserActivityLogs(payload);
    const apiLogs = response.data.logs || [];

    const formattedLogs: logEntry[] = apiLogs.map(
      (
        log: {
          user_email: string;
          event: string;
          details: string;
          timestamp: string;
          ip_address?: string;
        },
        index: number,
      ) => ({
        key: index.toString(),
        user: log.user_email,
        event: log.event,
        details: log.details,
        dateTime: log.timestamp,
        ipAddress: log.ip_address || "N/A",
      })
    );

    setLogsData(formattedLogs);
  }catch (error) {
      setError("Failed to fetch activity logs.");
      console.error("Failed to fetch activity logs", error);
    } finally {
      setLoading(false);
    }
};
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, eventFilter, dateRange]);

  useEffect(() => {

    fetchActivityLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

const filteredData = React.useMemo(() =>
  logsData.filter(log => {
    const user = log.user || "";
    const details = log.details || "";
    const search = searchText || "";

    const matchesSearch =
      user.toLowerCase().includes(search.toLowerCase()) ||
      details.toLowerCase().includes(search.toLowerCase());

    const matchesEvent = eventFilter
      ? log.event.toLowerCase() === (eventFilter || "").toLowerCase()
      : true;

    return matchesSearch && matchesEvent;
  }), [logsData, searchText, eventFilter]
);

const eventOptions = React.useMemo(() => [
  "USER MANAGEMENT",
  "AUTHENTICATION",
  "PASSWORD MANAGEMENT",
  "SOURCE MANAGEMENT",
  "NOTIFICATION MANAGEMENT",
  "Dashboard Viewed"
].map(event => ({
  label: toTitleCase(event),
  value: event
})), []);

  return (
    <div className="admin-portal-page" >
      <span className="heading-primary">
        Activity Logs
      </span>
      {error && (
      <div style={{ color: "red", marginBottom: 12 }}>{error}</div>
    )} 

<div className="filters-container">
  <div className="filters-inner">
    <StyledInput
  placeholder="Search by user or details..."
  value={searchText}
  onChange={e => setSearchText(e.target.value)}
  className="admin-search"
/>

    <StyledDropdown
      placeholder="All Events"
      value={eventFilter}
      onChange={value => setEventFilter(value)}
      allowClear
      options={eventOptions}
      style={{ minWidth: "150px" }}
      popupClassName="custom-dropdown-menu"
    />

    <RangePicker
      value={dateRange}
      onChange={dates => setDateRange(dates)}
      disabledDate={current => current && current > dayjs().endOf("day")}
    />
  </div>

  <div className="right-aligned-btns">
    <Button
      onClick={resetFilters}
      className="button button--primary"
      aria-label="Reset Filters"
      title="Reset Filters"
    >
      Reset
    </Button>
    <Button
      onClick={() => exportToCSV(filteredData)}
      className="button button--primary"
      disabled={filteredData.length === 0 || loading}
      aria-label="Export Logs"
      title="Export Logs"
      loading={loading}
    >
      Export Logs
    </Button>
  </div>
</div>

      
<StyledTable
  columns={columns}
  dataSource={filteredData}
  rowKey="key"
  pagination={{
    current: currentPage,
    pageSize,
    onChange: (page: number) => setCurrentPage(page),
    showSizeChanger: false,
  }}
  bordered
  loading={{ spinning: loading, indicator: <LoadingSpinner /> }}
  className="pipeline-table"
/>

    </div>
  );
};

export default ActivityLogs;
